import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import { animateScroll as scroll } from "react-scroll";
import _ from "lodash";
import sort from "fast-sort";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { State } from "src/state/ducks";
import * as OrdersActions from "src/state/ducks/order/actions";
import * as bakedBreadActions from "src/state/ducks/bakedBread/actions";
import OrdersDB, { OrderEntity } from "src/firebase/db/Orders";
import { UserEntity } from "src/firebase/db/Users";

import { formatDate, formatDateToDate } from "src/helpers/UtilityHelper";
import { isReservation } from "src/helpers/OrderHelper";
import StyleConfig from "src/constants/StyleConfig";
import { DefaultPageProps, HistoryProps } from "src/constants/Interface";

import { DefaultLayout } from "src/components/layouts/DefaultLayout";
import HomeButton from "src/components/atoms/button/HomeButton";
import { LinkButton } from "src/components/atoms/button/Button";
import SpinnerModal from "src/components/organisms/loading/SpinnerModal";
import OrderList from "src/components/organisms/list/OrderList";
import OrderFilterForm from "src/components/organisms/form/OrderFilterForm";

dayjs.extend(isSameOrAfter);

type OwnProps = {
  id: string;
  date: string;
};

type Props = DefaultPageProps &
  HistoryProps &
  OwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

const OrderSearchListPage = (props: Props) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [allSuggestions, setAllSuggestions] = useState<string[]>([]);
  const [filterByStatus, setFilterByStatus] = useState("準備する商品を表示");
  const [filterByPickup, setFilterByPickup] = useState(true);
  const [filterByReserved, setFilterByReserved] = useState(true);

  const {
    id: currentShopId,
    date,
    isFetching,
    orders,
    bakedBreads,
    users,
    usersOrders,
    getReservationBakedBreads,
    getDayOrders,
    updateIsConfirmed,
    updateIsCompleted,
  } = props;

  // GETパラメータの文字列をDateオブジェクトにしておく
  const from = useMemo(() => dayjs(date).startOf("date").toDate(), [date]);
  const until = useMemo(() => dayjs(date).endOf("date").toDate(), [date]);

  const fetch = useCallback(() => {
    getDayOrders(currentShopId, from, until);
  }, [currentShopId, from, until, getDayOrders]);

  useEffect(() => {
    getReservationBakedBreads(currentShopId);
  }, [currentShopId, getReservationBakedBreads]);

  // 初期表示：URIから取得した日付内の通知を取得
  useEffect(() => {
    fetch();

    // 対象データの監視
    const unsubscribe = OrdersDB.getConnection()
      .where("shop_id", "==", currentShopId)
      .where("created_at", ">=", from)
      .where("created_at", "<=", until)
      .where("status", "==", "succeeded")
      .orderBy("created_at", "desc")
      .onSnapshot((snapshots) => {
        snapshots.docChanges().forEach((change) => {
          if (change.type === "added") {
            fetch();
          }
        });
      });
    return () => unsubscribe();
  }, [fetch, currentShopId, from, until]);

  // 初期表示：オートコンプリート用の配列取得
  useEffect(() => {
    const orderNumbers = orders.map(({ order_number }) => order_number);
    const userNames = users.map(({ name }) => name);
    const values = _.concat(orderNumbers, userNames);
    const sorted = sort(_.uniq(values)).asc();
    setSuggestions(sorted);
    setAllSuggestions(sorted);
  }, [orders, users, setSuggestions, setAllSuggestions]);

  // URIから取得した日付
  const currentDate = useMemo(() => formatDate(date, "YYYY年MM月DD日（ddd）"), [
    date,
  ]);

  // 対象注文以前の注文を取得
  const filterPrevOrders = useCallback(
    (order: OrderEntity) => {
      const { user_id, created_at } = order;
      const date = dayjs(formatDateToDate(created_at));
      return _(usersOrders)
        .filter(
          (order) =>
            order.user_id === user_id &&
            order.shop_id === currentShopId &&
            order.status === "succeeded" &&
            date.isSameOrAfter(formatDateToDate(order.created_at))
        )
        .value() as OrderEntity[];
    },
    [usersOrders, currentShopId]
  );

  // 注文に紐づくユーザーを取得
  const filterUsers = useCallback(
    (order: OrderEntity) => {
      const { user_id } = order;
      return users.find((user) => user.user_id === user_id) as UserEntity;
    },
    [users]
  );

  // クエリを配列に変換
  const queries = useMemo(() => {
    const str = query.replace(/　/g, " ");
    return _(str.split(" "))
      .uniq()
      .remove((v) => v)
      .value();
  }, [query]);

  // 注文に紐づくユーザーを取得
  const isMathedQuery = useCallback(
    (target: string) => {
      return queries.some((q) => target.indexOf(q.toLowerCase()) !== -1);
    },
    [queries]
  );

  // 検索条件で絞り込んだ注文データ
  const filteredOrders = useMemo(() => {
    if (!queries.length || queries[0] === "") {
      return orders;
    }

    // 検索クエリに一致するデータを返す
    return orders.filter((order) => {
      const user = filterUsers(order);
      const userName = user?.name || "";
      const orderNumber = order?.order_number || "";
      const isMathedOrderNumber = isMathedQuery(orderNumber.toLowerCase());
      const isMathedUserName = isMathedQuery(userName.toLowerCase());
      return queries.length > 1
        ? isMathedOrderNumber && isMathedUserName
        : isMathedOrderNumber || isMathedUserName;
    });
  }, [orders, queries, filterUsers, isMathedQuery]);

  const onUpdateIsConfirmed = useCallback(
    (id: string, isConfirmed: boolean) => {
      updateIsConfirmed(id, isConfirmed);
      fetch();
    },
    [updateIsConfirmed, fetch]
  );

  const onUpdateIsCompleted = useCallback(
    (id: string, isCompleted: boolean) => {
      updateIsCompleted(id, isCompleted);
      fetch();
    },
    [updateIsCompleted, fetch]
  );

  // 検索クエリの変更
  const onChangeQuery = useCallback(
    (v: string) => {
      setQuery(v);
    },
    [setQuery]
  );

  // 検索クエリが選択された時
  const onSuggestionSelected = useCallback(
    (v: string) => {
      setQuery(v);
    },
    [setQuery]
  );

  // 検索クエリがリセットされた時
  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([]);
  }, [setSuggestions]);

  // 入力される度にサジェストの中身を更新する
  const onSuggestionsFetchRequested = useCallback(
    (v: string) => {
      const inputValue = v.trim().toLowerCase();
      const inputLength = inputValue.length;

      const filtered =
        inputLength === 0 || !allSuggestions
          ? []
          : allSuggestions.filter(
              (v) => v.toLowerCase().slice(0, inputLength) === inputValue
            );

      setSuggestions(filtered);
    },
    [allSuggestions, setSuggestions]
  );

  // ステータスフィルター
  const onChangeFilterByStatus = useCallback(
    (value) => {
      setFilterByStatus(value);
    },
    [setFilterByStatus]
  );

  // 種別フィルター
  const onChangeFilterByPickup = useCallback(() => {
    setFilterByPickup(!filterByPickup);
  }, [filterByPickup, setFilterByPickup]);
  const onChangeFilterByReserved = useCallback(() => {
    setFilterByReserved(!filterByReserved);
  }, [filterByReserved, setFilterByReserved]);

  return (
    <DefaultLayout {...props}>
      <SpinnerModal isLoading={isFetching} />
      <Container>
        <button onClick={fetch}>
          <Header>{`${currentDate}の注文状況を確認する`}</Header>
        </button>
        <OrderFilterForm
          suggestions={suggestions}
          query={query}
          filterByStatus={filterByStatus}
          filterByPickup={filterByPickup}
          filterByReserved={filterByReserved}
          onChangeQuery={onChangeQuery}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          onChangeFilterByStatus={onChangeFilterByStatus}
          onChangeFilterByPickup={onChangeFilterByPickup}
          onChangeFilterByReserved={onChangeFilterByReserved}
        />
        <div className="orderList">
          <OrderList
            isLoading={isFetching}
            orders={filteredOrders}
            bakedBreads={bakedBreads}
            filterByStatus={filterByStatus}
            filterByPickup={filterByPickup}
            filterByReserved={filterByReserved}
            filterUsers={filterUsers}
            filterPrevOrders={filterPrevOrders}
            onUpdateIsConfirmed={onUpdateIsConfirmed}
            onUpdateIsCompleted={onUpdateIsCompleted}
          />
        </div>
        <div className="item">
          <LinkButton
            isDisabled={false}
            url={`/shop/${currentShopId}}/search/orders`}
            text={"別の日付の注文状況を確認する"}
          />
        </div>
        <div className="item">
          <HomeButton />
        </div>
      </Container>
      <ToTopButton
        onClick={() => {
          scroll.scrollToTop({ duration: 300 });
        }}
      >
        <img src="/images/icon/to_top.png" alt="TOPへスクロール" />
      </ToTopButton>
    </DefaultLayout>
  );
};

const Header = styled.h1`
  font-size: 16px;
  line-height: 24px;
  font-weight: bold;
`;

const Container = styled.div`
  box-sizing: border-box;
  padding: ${StyleConfig.HELPER.M20};
  button {
    margin-bottom: 20px;
  }
  > .lead {
    font-size: 12px;
  }
  > .item {
    margin: ${StyleConfig.HELPER.M24} auto;
  }
  > .orderList {
    margin-top: ${StyleConfig.HELPER.M32};
  }
`;

const ToTopButton = styled.button`
  position: fixed;
  right: 16px;
  bottom: 24px;
`;

const mapStateToProps = (state: State, ownProps: OwnProps) => {
  const bakedBreads = state.bakedBread.resource;

  // 通常商品に加え、当日受取予定の予約商品も取得する
  const { date } = ownProps;
  const filtered = state.order.filterByDate.resource.filter((v) => {
    const isReservationOrder = isReservation(v, bakedBreads);
    const lastPickupDate = dayjs(formatDateToDate(v.last_pickup_time));
    return (
      !isReservationOrder ||
      (isReservationOrder && lastPickupDate.isSame(date, "day"))
    );
  });
  const orders = sort(filtered).by([
    { asc: (v) => isReservation(v, bakedBreads) },
    { desc: ({ created_at }) => formatDateToDate(created_at).getTime() },
  ]);

  return {
    orders,
    bakedBreads,
    users: state.user.resource,
    usersOrders: state.order.filterByUsers.resource,
    isFetching:
      state.order.filterByDate.isFetching ||
      state.order.isFetching ||
      state.bakedBread.isFetching,
    error: state.order.filterByDate.apiError,
    ordersError: state.order.filterByDate.apiError,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getReservationBakedBreads: (shopId: string) =>
    dispatch(bakedBreadActions.getReservationBakedBreads(shopId)),
  getDayOrders: (id: string, startDate: Date, endDate: Date) =>
    dispatch(
      OrdersActions.getDayOrders({
        shopId: id,
        startDate: startDate,
        endDate: endDate,
        target: "last_pickup_time",
      })
    ),
  updateIsConfirmed: (id: string, isConfirmed: boolean) =>
    dispatch(
      OrdersActions.updateOrderIsConfirmed({
        id,
        isConfirmed,
      })
    ),
  updateIsCompleted: (id: string, isCompleted: boolean) =>
    dispatch(
      OrdersActions.updateOrderIsCompleted({
        id,
        isCompleted,
      })
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderSearchListPage);
